import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getAccommodationsBlurPlaceholder } from "@/utils/accommodationsBlurPlaceholders";
import { Room } from "@/types/room";
import {
  Users,
  Square,
  Bed,
  Bath,
  ChevronLeft,
  ChevronRight,
  Mountain,
  Wifi,
  Tv,
  Wind,
  ChefHat,
  Shield,
  Star,
  Share2,
  Coffee,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useViewportAutoplay } from "@/hooks/useViewportAutoplay";

interface JungfrauSingleSectionProps {
  room: Room;
  onViewDetails?: (roomId: string) => void;
  onQuickBook?: (roomId: string) => void;
}

const JungfrauSingleSection: React.FC<JungfrauSingleSectionProps> = ({
  room,
  onViewDetails,
  onQuickBook,
}) => {
  const { t } = useLanguage();

  // Viewport-based auto-play hook
  const {
    currentImageIndex,
    isAutoPlaying,
    elementRef,
    handlePreviousImage,
    handleNextImage,
    handleImageClick,
  } = useViewportAutoplay({
    imageCount: room.images.length,
    intervalMs: 4000,
    threshold: 0.7,
  });

  const handleViewDetails = () => {
    onViewDetails?.(room.id);
  };

  const handleQuickBook = () => {
    onQuickBook?.(room.id);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/accommodations#jungfrau-single`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${room.name} - Park Hotel Beatenberg`,
          text: `Check out our Jungfrau Single Rooms with private balcony and stunning lake views`,
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard if share is cancelled
        copyToClipboard(url);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const currentImage = room.images[currentImageIndex];

  // Feature badges data
  const featureBadges = [
    {
      icon: Users,
      label: "Occupancy",
      value: t("rooms.jungfrauSingleBadges.occupancy"),
      description: `${room.capacity.adults} adults, ${room.capacity.children} children`,
    },
    {
      icon: Square,
      label: "Room Size",
      value: t("rooms.jungfrauSingleBadges.roomSize"),
      description: `${room.size.squareFeet} sq ft`,
    },
    {
      icon: Bed,
      label: "Bedding",
      value: t("rooms.jungfrauSingleBadges.bedding"),
      description: "Comfortable single bed",
    },
    {
      icon: Bath,
      label: "Bathrooms",
      value: t("rooms.jungfrauSingleBadges.bathrooms"),
      description: "Ensuite bathroom",
    },
  ];

  return (
    <section
      id="jungfrau-single"
      className="py-16 bg-gradient-to-b from-white to-gray-50/50 relative overflow-hidden"
      data-oid="a_bv_pk"
    >
      {/* Background Quadrilaterals */}
      <div
        className="absolute top-0 right-0 w-96 h-96 transform rotate-45 translate-x-48 -translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="qekdo44"
      ></div>
      <div
        className="absolute bottom-0 left-0 w-96 h-96 transform rotate-45 -translate-x-48 translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="9_qamkt"
      ></div>
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        data-oid="n761czr"
      >
        {/* Mobile Header - Shown only on mobile */}
        <div className="mb-8 lg:hidden" data-oid="zbt3_m8">
          <div className="mb-4" data-oid="xbpogpp">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-playfair text-gray-900"
              data-oid="jj6a5-b"
            >
              {t("rooms.jungfrauSingle")}
            </h2>
          </div>

          {/* Key badges below title - Mobile */}
          <div
            className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap"
            data-oid="4tb5hl4"
          >
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="nzol5zt"
            >
              <Users
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="n:8omci"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="q8pi.a1"
              >
                {featureBadges[0].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="kqw-1at"
            >
              <Square
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="9x9av--"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="0vl43fu"
              >
                {featureBadges[1].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="3b.k90s"
            >
              <Bed
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="0go2bl_"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="fvyh:z2"
              >
                {featureBadges[2].value}
              </span>
            </div>
          </div>
        </div>

        {/* 2-Column Layout: Carousel Left (60%), Content Right (40%) */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[50%_50%] xl:grid-cols-[55%_45%] 2xl:grid-cols-[60%_40%] gap-6 lg:gap-8 xl:gap-12 mb-12"
          data-oid="1w9628h"
        >
          {/* Left Column - Image Carousel */}
          <div
            ref={elementRef}
            className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl"
            data-oid="9b096p8"
          >
            <div
              className="flex h-full transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              data-oid="amuhgym"
            >
              {room.images.map((image, index) => (
                <div
                  key={image.id}
                  className="w-full h-full flex-shrink-0"
                  data-oid="a548i7:"
                >
                  <OptimizedImage
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    priority={false}
                    rootMargin="200px"
                    blurDataURL={getAccommodationsBlurPlaceholder(image.url)}
                    responsive={true}
                    data-oid="r24sjmb"
                  />
                </div>
              ))}
            </div>

            {/* Image Navigation */}
            {room.images.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handlePreviousImage}
                  aria-label="Previous image"
                  data-oid="u1._h7c"
                >
                  <ChevronLeft className="w-5 h-5" data-oid="sijxsv1" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  data-oid="r1kiu1j"
                >
                  <ChevronRight className="w-5 h-5" data-oid="web52c3" />
                </Button>
              </>
            )}

            {/* Image Indicators */}
            {room.images.length > 1 && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
                data-oid="aujj_qd"
              >
                {room.images.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all duration-300",
                      index === currentImageIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/75",
                    )}
                    onClick={() => handleImageClick(index)}
                    aria-label={`Go to image ${index + 1}`}
                    data-oid="-fvy:mm"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Content */}
          <div
            className="flex flex-col gap-8 min-w-0 overflow-hidden pr-4 lg:pr-6 xl:pr-8"
            data-oid="ebot2v_"
          >
            {/* Desktop Header - Shown only on desktop */}
            <div className="hidden lg:block" data-oid="g69wfux">
              <div className="mb-4" data-oid="pmfquzs">
                <h2
                  className="text-3xl xl:text-4xl 2xl:text-5xl font-playfair text-gray-900"
                  data-oid="uccra27"
                >
                  {t("rooms.jungfrauSingle")}
                </h2>
              </div>

              {/* Key badges below title - Desktop */}
              <div
                className="flex items-center gap-2 lg:gap-3 xl:gap-4 2xl:gap-6 flex-wrap"
                data-oid="04s0sed"
              >
                <div className="flex items-center gap-2" data-oid=":qtt8t-">
                  <Users
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="9:f4jxo"
                  />

                  <span className="text-sm text-gray-900" data-oid="b.ojw1h">
                    {featureBadges[0].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="zigbo:v">
                  <Square
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="ucr5969"
                  />

                  <span className="text-sm text-gray-900" data-oid="4e0.u02">
                    {featureBadges[1].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="05_k232">
                  <Bed
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="l9pqpsl"
                  />

                  <span className="text-sm text-gray-900" data-oid="6cqu63x">
                    {featureBadges[2].value}
                  </span>
                </div>
              </div>
            </div>

            {/* 1. Description */}
            <div className="overflow-hidden" data-oid="013kjgv">
              <p
                className="text-sm lg:text-base xl:text-lg text-gray-700 leading-relaxed break-words"
                data-oid="mess.ku"
              >
                <span className="sm:hidden" data-oid="cohri16">
                  {t("rooms.jungfrauSingleDescription.mobile")}
                </span>
                <span className="hidden sm:inline" data-oid="z5.1.v0">
                  {t("rooms.jungfrauSingleDescription.desktop")}
                </span>
              </p>
            </div>

            {/* 2. All Features & Amenities - Consolidated */}
            <div className="grid grid-cols-1 gap-y-4" data-oid="n-4ktw3">
              {/* Private balcony with lake views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="flk5s:q"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="_re895q"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="zgrz330"
                >
                  {t("rooms.jungfrauSingleFeatures.balconyWithLakeView")}
                </span>
              </div>

              {/* Perfect for solo travelers */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="-6uh1pn"
              >
                <Users
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="39xn9y6"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="rfv-fn6"
                >
                  {t("rooms.jungfrauSingleFeatures.perfectForSoloTravelers")}
                </span>
              </div>

              {/* Free Wifi */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="0s5e42o"
              >
                <Wifi
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="8bgvrp:"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="enzw-px"
                >
                  Free Wifi
                </span>
              </div>

              {/* Flatscreen TV */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="t03azuk"
              >
                <Tv
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="mz7-vxo"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="ezfczxc"
                >
                  Flatscreen TV
                </span>
              </div>

              {/* Premium organic bio cotton bedding */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="av9cm_o"
              >
                <Star
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="vjjcvur"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="k.i0bz5"
                >
                  Premium organic bio cotton bedding
                </span>
              </div>

              {/* Ensuite coffee machine */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="ki3d310"
              >
                <Coffee
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="g:hvpyh"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="r20gk.y"
                >
                  Ensuite coffee machine
                </span>
              </div>
            </div>

            {/* 3. Book Now and Share Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              data-oid=":ebf6e-"
            >
              <Button
                asChild
                size="lg"
                className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                trackingType="booking"
                trackingLabel={t("nav.bookNow")}
                trackingLocation="Jungfrau Single Section"
                data-oid="nf94zxu"
              >
                <a
                  href={import.meta.env.VITE_BOOKING_URL}
                  data-oid="f5h-fb9"
                >
                  {t("nav.bookNow")}
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="bg-white/80 hover:bg-alpine-gold hover:text-white border-gray-300 hover:border-alpine-gold transition-all duration-300 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105"
                onClick={handleShare}
                aria-label="Share Jungfrau Single Rooms"
                data-oid="j1hakg_"
              >
                <Share2 className="w-4 h-4 mr-2" data-oid="24-9c7u" />
                {t("common.share")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JungfrauSingleSection;
